import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AngularEditorConfig, AngularEditorModule } from '@kolkov/angular-editor';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { IssueTrackerIncidenceDataDTO, IssueTrackerIncidenceDataPostDTO } from '../../../models/issue-tracker-incidence-data-dto';
import { IssueTrackerEmailStatusDTO } from '../../../models/issue-tracker-email-status-dto';
import { IssueTrackerIncidenceService } from '../../../services/issue-tracker-incidence.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';
import { Helpers } from '../../../helpers/helpers';

@Component({
  selector: 'app-incidence-message-list',
  templateUrl: './incidence-message-list.component.html',
  styleUrls: ['./incidence-message-list.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, AngularEditorModule, NgxDropzoneModule]
})
export class IncidenceMessageListComponent implements OnInit, OnChanges {

  @Input() messages: IssueTrackerIncidenceDataDTO[];
  @Input() isAdmin: boolean = false;
  /** Firma del proyecto (HTML), se pinta al final de los mensajes públicos de gestor */
  @Input() signature: string | null = null;
  @ViewChild('modalContent') modalContent?: TemplateRef<unknown>;

  emailStatuses: Map<string, IssueTrackerEmailStatusDTO> = new Map();
  loadingStatuses: Set<string> = new Set();

  /** Id del mensaje en edición; null si no hay ninguno */
  editingMessageId: string | null = null;
  editedMessageContent: string = '';
  /** Archivo adjunto al editar (reemplazo opcional) */
  editFileAttachedFiles: File[] = [];
  /** Marcar mensaje como interno al guardar */
  editedIsInternal: boolean = false;

  modalPreviewFileName: string = '';
  modalPreviewFileUri: string = '';
  modalPreviewFileSize: number = 0;

  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '180px',
    minHeight: '0',
    maxHeight: '180px',
    width: 'auto',
    minWidth: '0',
    translate: 'yes',
    enableToolbar: true,
    showToolbar: true,
    placeholder: 'Introduzca el mensaje aquí...',
    uploadWithCredentials: false,
    sanitize: true,
    toolbarPosition: 'top',
    toolbarHiddenButtons: [
      [
        'strikeThrough',
        'subscript',
        'superscript',
        'heading',
        'fontName',
        'fontSize',
        'textColor',
        'backgroundColor',
        'customClasses',
        'link',
        'unlink',
        'insertImage',
        'insertVideo',
        'insertHorizontalRule',
        'removeFormat',
        'toggleEditorMode',
        'insertOrderedList',
        'justifyFull'
      ]
    ]
  };

  constructor(
    private service: IssueTrackerIncidenceService,
    private cdr: ChangeDetectorRef,
    private modalService: NgbModal,
    private helper: Helpers
  ) { }

  ngOnInit() {
    if (this.isAdmin && this.messages) {
      this.loadEmailStatuses();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['messages'] && this.isAdmin && this.messages) {
      this.loadEmailStatuses();
    }
    if (changes['isAdmin'] && this.isAdmin && this.messages) {
      this.loadEmailStatuses();
    }
  }

  private loadEmailStatuses() {
    if (!this.isAdmin || !this.messages) {
      return;
    }

    // Cargar el estado del email solo para mensajes del gestor (no internos y no del usuario)
    this.messages.forEach(message => {
      if (!message.IsInternal && !message.MessageFromUser && message.IdData) {
        this.getEmailStatus(message.IdData);
      }
    });
  }

  private getEmailStatus(idData: string) {
    // Evitar cargar el mismo estado múltiples veces
    if (this.loadingStatuses.has(idData) || this.emailStatuses.has(idData)) {
      return;
    }

    this.loadingStatuses.add(idData);

    this.service.getEmailStatus(idData).subscribe({
      next: (status) => {
        this.emailStatuses.set(idData, status);
        this.loadingStatuses.delete(idData);
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error al obtener el estado del email:', err);
        this.loadingStatuses.delete(idData);
      }
    });
  }

  getEmailStatusForMessage(message: IssueTrackerIncidenceDataDTO): IssueTrackerEmailStatusDTO | null {
    if (!this.isAdmin || message.IsInternal || message.MessageFromUser) {
      return null;
    }
    return this.emailStatuses.get(message.IdData) || null;
  }

  isLoadingEmailStatus(idData: string): boolean {
    return this.loadingStatuses.has(idData);
  }

  getMessageClass(message: IssueTrackerIncidenceDataDTO): string {
    let className = '';
    if (message.Message.includes('change-status-container')) {
      className = 'change-status-message';
    }
    if (message.IsInternal) {
      className += ' internal-message';
    } else if (message.MessageFromUser) {
      className += ' user-message';
    } else {
      className += ' manager-message';
    }
    return className;
  }

  /** Solo mensajes de gestor visibles para el usuario; quedan fuera los cambios de estado (incluidos los de Sistema) */
  showSignature(message: IssueTrackerIncidenceDataDTO): boolean {
    return !!this.signature
      && !message.IsInternal
      && !message.MessageFromUser
      && !message.Message.includes('change-status-container');
  }

  getMessageLabel(message: IssueTrackerIncidenceDataDTO): string {
    if (message.IsInternal) {
      return 'Mensaje Interno';
    } else if (message.MessageFromUser) {
      return 'Usuario';
    } else {
      return 'Gestor';
    }
  }

  getTimeAgo = (creationDate: string | Date | undefined): string => {
    if (!creationDate) return '';
    let date: Date;
    if (typeof creationDate === 'string') {
      // Formato: "DD/MM/YYYY HH:mm"
      const match = creationDate.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})$/);
      if (match) {
        const [, day, month, year, hour, min] = match;
        date = new Date(+year, +month - 1, +day, +hour, +min, 0, 0);
      } else {
        date = new Date(creationDate);
      }
    } else {
      date = creationDate;
    }
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) {
      return `Hace ${diffSec} ${diffSec === 1 ? 'segundo' : 'segundos'}`;
    } else if (diffMin < 60) {
      return `Hace ${diffMin} ${diffMin === 1 ? 'minuto' : 'minutos'}`;
    } else if (diffHour < 24) {
      return `Hace ${diffHour} ${diffHour === 1 ? 'hora' : 'horas'}`;
    } else {
      return `Hace ${diffDay} ${diffDay === 1 ? 'día' : 'días'}`;
    }
  };

  formatFileSize(bytes: number): string {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  isImageFile(file: any): boolean {
    if (!file || !file.FileExtension) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    return imageExtensions.includes(file.FileExtension.toLowerCase());
  }

  fixAddMessageToBottom() {
    const addMessageElement = document.getElementById('add-message');
    const bodyElement = document.getElementById('issue-tracker-main-container');
    if (addMessageElement.classList.contains('fixed-bottom-message')) {
      addMessageElement.classList.add('hidding-bottom-message');
      setTimeout(() => {
        addMessageElement.classList.remove('hidding-bottom-message');
        addMessageElement.classList.remove('fixed-bottom-message');
        bodyElement.classList.remove('add-message-shown');
        bodyElement.style.marginBottom = '0';
        console.log('bodyElement remove', bodyElement.classList);
      }, 300);
    } else {
      addMessageElement.classList.add('fixed-bottom-message');
      bodyElement.classList.add('add-message-shown');
      bodyElement.style.marginBottom = addMessageElement.offsetHeight + 64 + 'px';
      console.log('bodyElement add', bodyElement.classList);
    }
  }

  openPreviewModal(fileName: string, fileUri: string, fileSize: number): void {
    this.modalPreviewFileName = fileName;
    this.modalPreviewFileUri = fileUri;
    this.modalPreviewFileSize = fileSize;

    if (!this.modalContent) return;
    this.modalService.open(this.modalContent, {
      ariaLabelledBy: 'modal-basic-title',
      size: 'xl',
      centered: true,
      windowClass: 'modal-fullscreen'
    });
  }

  editMessage(message: IssueTrackerIncidenceDataDTO) {
    this.editingMessageId = message.IdData;
    this.editedMessageContent = message.Message ?? '';
    this.editedIsInternal = message.IsInternal ?? false;
    this.editFileAttachedFiles = [];
    this.cdr.detectChanges();
  }

  cancelEdit(): void {
    this.editingMessageId = null;
    this.editedMessageContent = '';
    this.editFileAttachedFiles = [];
  }

  onEditFileAttachedSelect(event: { addedFiles: File[] }): void {
    if (this.editFileAttachedFiles.length > 0) {
      this.editFileAttachedFiles.splice(0, 1);
    }
    this.editFileAttachedFiles.push(...event.addedFiles);
  }

  onEditFileAttachedRemove(file: File): void {
    this.editFileAttachedFiles.splice(this.editFileAttachedFiles.indexOf(file), 1);
  }

  saveEdit(): void {
    if (!this.editingMessageId || !this.messages) return;
    const msg = this.messages.find(m => m.IdData === this.editingMessageId);
    if (msg) {
      msg.Message = this.editedMessageContent ?? '';
      msg.IsInternal = this.editedIsInternal;

      if (msg.Message.trim() === '') {
        Swal.fire({
          icon: 'warning',
          title: 'Mensaje vacío',
          text: 'Por favor, escriba un mensaje antes de enviar'
        });
        return;
      }

      let postModel: IssueTrackerIncidenceDataPostDTO = new IssueTrackerIncidenceDataPostDTO();
      postModel.IdIncidence = msg.IdIncidence;
      postModel.Message = msg.Message.trim();
      postModel.IsInternal = msg.IsInternal;

      if (this.editFileAttachedFiles.length > 0)
        postModel.FileAttachedFile = this.editFileAttachedFiles[0];

      var data = this.helper.getFormData(postModel);

      Swal.fire({
        allowOutsideClick: false,
        icon: 'info',
        text: 'Espere por favor...'
      });
      Swal.showLoading();

      this.updateMessage(this.editingMessageId, data);
    }

    this.editingMessageId = null;
    this.editedMessageContent = '';
    this.editFileAttachedFiles = [];
    this.cdr.detectChanges();
  }

  updateMessage(idMessage: string, data: FormData) {
    this.service.updateMessage(idMessage, data).subscribe({
      next: (resp) => {
        Swal.close();
        const existing = this.messages?.find(m => m.IdData === idMessage);
        if (existing) {
          //Como la api devuelve un id cifrado diferente al original, se debe migrar el estado del email al nuevo id
          const oldId = existing.IdData;
          Object.assign(existing, resp);
          const newId = (resp as IssueTrackerIncidenceDataDTO).IdData;
          if (newId && newId !== oldId) {
            const status = this.emailStatuses.get(oldId);
            if (status) {
              this.emailStatuses.set(newId, status);
              this.emailStatuses.delete(oldId);
            }
            this.loadingStatuses.delete(oldId);
          }
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        Swal.fire({
          icon: 'error',
          title: 'Error al actualizar el mensaje',
          text: 'Ocurrió un error'
        });
      }
    });
  }

  isEditingMessage(message: IssueTrackerIncidenceDataDTO): boolean {
    return this.editingMessageId === message.IdData;
  }

  deleteFileAttached(message: IssueTrackerIncidenceDataDTO) {
    Swal.fire({
      icon: 'warning',
      title: 'Eliminar archivo adjunto',
      text: '¿Estás seguro de querer eliminar el archivo adjunto?',
      buttonsStyling: false,
      customClass: {
        cancelButton: 'btn-outline-black',
        confirmButton: 'btn-red'
      },
      showCancelButton: true,
      allowOutsideClick: false,
      confirmButtonText: 'Eliminar <i class="fa-solid fa-trash-can"></i>',
      cancelButtonText: 'Cancelar <i class="fa-solid fa-xmark"></i>',
      reverseButtons: true,
      focusCancel: true
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          allowOutsideClick: false,
          icon: 'info',
          text: 'Espere por favor...'
        });
        Swal.showLoading();

        this.service.deleteFileAttachedMessage(message.IdData).subscribe({
          next: (resp) => {
            message.FileAttached = null;
            this.cdr.detectChanges();
            Swal.close();
          },
          error: (err) => {
            Swal.fire({
              icon: 'error',
              title: 'Error al eliminar el archivo adjunto',
              text: 'Ocurrió un error'
            });
            Swal.close();
          }
        });
      }
    });
  }

}
